import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import './saved.css';
import {
  BottomNav,
  BUYER_NAV,
  ColorRail,
  HeartOutlineIcon,
  LockIcon,
  TopBar,
} from '../../../commodity/components/caria';
import { useAuthChrome } from '../../../core/identity-access/hooks/useAuthChrome';
import { fetchSavedListings, SavedListing, unsaveListing } from '../api/saved-listings.api';

/**
 * SavedListings (T0 Epic E.2) — the buyer's saved listings. Reads
 * GET /saved-listings; allows removing a save.
 *
 * Ported onto the CAR-IA chrome and the prototype's content rail + row-card
 * language (docs/html-prototypes/CAR-IA). It previously rendered inside the
 * pre-lime forest-green MainLayout — sidebar, English footer — with no CAR-IA
 * chrome, and surfaced raw API errors verbatim: a seller hitting this route saw
 * the English string "Insufficient permissions", because GET /saved-listings is
 * `@Roles(Role.BUYER)`. That 403 is correct (saved listings are a buyer
 * feature); showing it as a raw English error was not.
 */

type State =
  | { kind: 'loading' }
  | { kind: 'forbidden' }
  | { kind: 'error'; message: string }
  | { kind: 'ready'; rows: SavedListing[] };

const isForbidden = (err: unknown): boolean => {
  const msg = err instanceof Error ? err.message : String(err ?? '');
  return /insufficient permissions|forbidden|\b403\b/i.test(msg);
};

const rd = (n: number) => `RD$ ${n.toLocaleString('es-DO')}`;

export const SavedListings: React.FC = () => {
  const navigate = useNavigate();
  const authChrome = useAuthChrome();
  const [state, setState] = useState<State>({ kind: 'loading' });

  useEffect(() => {
    let active = true;
    fetchSavedListings()
      .then((rows) => active && setState({ kind: 'ready', rows }))
      .catch((err) => {
        if (!active) return;
        setState(
          isForbidden(err)
            ? { kind: 'forbidden' }
            : {
                kind: 'error',
                message:
                  err instanceof Error ? err.message : 'No se pudieron cargar tus guardados.',
              },
        );
      });
    return () => {
      active = false;
    };
  }, []);

  const remove = async (vehicleId: string) => {
    setState((s) =>
      s.kind === 'ready' ? { ...s, rows: s.rows.filter((r) => r.vehicleId !== vehicleId) } : s,
    );
    try {
      await unsaveListing(vehicleId);
    } catch {
      // best-effort; a reload restores the true state
    }
  };

  const rows = state.kind === 'ready' ? state.rows : [];

  return (
    <div className="cia-saved">
      <TopBar {...authChrome} nav={BUYER_NAV} />
      <ColorRail />

      <div className="cia-saved-wrap">
        <div className="cia-saved-head">
          <h1>Guardados</h1>
          {state.kind === 'ready' && rows.length > 0 && (
            <span className="cia-saved-count">{rows.length}</span>
          )}
        </div>
        <p className="cia-saved-sub">Los vehículos que marcaste para volver a verlos.</p>

        {state.kind === 'loading' && (
          <div className="cia-saved-state" data-testid="saved-loading" aria-busy="true">
            <p>Cargando…</p>
          </div>
        )}

        {state.kind === 'forbidden' && (
          <div className="cia-saved-state" data-testid="saved-forbidden">
            <span className="cia-saved-state-ic">
              <LockIcon size={25} strokeWidth={1.8} />
            </span>
            <h2>Guardados es una función de comprador</h2>
            <p>
              Tu cuenta está registrada como vendedor, así que no tiene una lista de guardados.
              Explora el catálogo con una cuenta de comprador para guardar vehículos.
            </p>
            <button className="cia-saved-cta" onClick={() => navigate('/search')}>
              Explorar el catálogo
            </button>
          </div>
        )}

        {state.kind === 'error' && (
          <div className="cia-saved-state" data-testid="saved-error">
            <h2>No pudimos cargar tus guardados</h2>
            <p role="alert">{state.message}</p>
            <button className="cia-saved-cta" onClick={() => navigate('/search')}>
              Explorar el catálogo
            </button>
          </div>
        )}

        {state.kind === 'ready' && rows.length === 0 && (
          <div className="cia-saved-state" data-testid="saved-empty">
            <span className="cia-saved-state-ic">
              <HeartOutlineIcon size={25} strokeWidth={1.8} />
            </span>
            <h2>Todavía no has guardado nada</h2>
            <p>Toca el corazón en cualquier anuncio para tenerlo aquí.</p>
            <button className="cia-saved-cta" onClick={() => navigate('/search')}>
              Explorar el catálogo
            </button>
          </div>
        )}

        {state.kind === 'ready' && rows.length > 0 && (
          <div className="cia-saved-list" data-testid="saved-listings">
            {rows.map((l) => (
              <div key={l.vehicleId} className="cia-saved-row">
                <span className="cia-saved-info">
                  <button
                    className="cia-saved-title"
                    onClick={() => navigate(`/vehicles/${l.vehicleId}`)}
                  >
                    {l.title}
                    {!l.available && <span className="cia-saved-gone">Ya no disponible</span>}
                  </button>
                  <span className="cia-saved-meta">Guardado</span>
                </span>
                {l.listingPrice != null && (
                  <span className="cia-saved-price">{rd(l.listingPrice)}</span>
                )}
                <button className="cia-saved-remove" onClick={() => remove(l.vehicleId)}>
                  Quitar
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      <BottomNav />
    </div>
  );
};

export default SavedListings;
